import { useEffect, useId, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Copy, Download, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatIban } from "@/lib/banking/czechAccount";
import { localePath, makeTr, type LandingLocale } from "@/lib/landing/locale";
import {
  buildQrPayment,
  QR_FIELD_MAX,
  type QrPaymentError,
  type QrPaymentInput,
} from "@/lib/qr/qrPayment";
import { track } from "@/lib/tracking/analytics";
import { cn } from "@/lib/utils";

/**
 * The free QR payment generator: a form to a Czech QR payment (SPD), shown as
 * it is typed, with PNG and SVG downloads and the SPD text to copy. Everything
 * happens in this tab; the QR library loads with the first valid payment.
 * After consent one `tool_used` event per download or copy, with nothing that
 * was typed.
 */

const EMPTY: QrPaymentInput = {
  account: "",
  amount: "",
  currency: "CZK",
  variableSymbol: "",
  constantSymbol: "",
  specificSymbol: "",
  message: "",
  recipientName: "",
  dueDate: "",
};

const ERRORS: Record<QrPaymentError, [string, string]> = {
  too_long: ["Příliš dlouhý text.", "Too long."],
  account_required: ["Vyplňte číslo účtu nebo IBAN.", "Enter an account number or IBAN."],
  account_invalid: [
    "Číslo účtu nesedí. Zkontrolujte číslo a kód banky (např. 19-2000145399/0800).",
    "The account number does not check out. Check the number and bank code (e.g. 19-2000145399/0800).",
  ],
  iban_invalid: ["IBAN nesedí, zkontrolujte ho.", "The IBAN does not check out."],
  iban_country: [
    "QR platba je pro české a slovenské účty (IBAN začínající CZ nebo SK).",
    "QR payments are for Czech and Slovak accounts (an IBAN starting with CZ or SK).",
  ],
  amount_invalid: [
    "Částku napište číslem, nejvýš se dvěma desetinnými místy.",
    "Write the amount as a number with at most two decimals.",
  ],
  amount_range: [
    "Částka musí být větší než nula a nejvýš 9 999 999,99.",
    "The amount must be above zero and at most 9,999,999.99.",
  ],
  symbol_invalid: ["Jen číslice, nejvýš 10.", "Digits only, at most 10."],
  date_invalid: ["Neplatné datum.", "Invalid date."],
};

function download(href: string, fileName: string) {
  const a = document.createElement("a");
  a.href = href;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  a.remove();
}

export function QrPaymentGenerator({ locale }: { locale: LandingLocale }) {
  const tr = makeTr(locale);
  const id = useId();
  const [input, setInput] = useState<QrPaymentInput>(EMPTY);
  // Errors show for a field once it has been left, or after a download attempt.
  const [touched, setTouched] = useState<Partial<Record<keyof QrPaymentInput, boolean>>>({});
  const [png, setPng] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [used, setUsed] = useState(false);

  const result = useMemo(() => buildQrPayment(input), [input]);
  const spd = "spd" in result ? result.spd : null;

  useEffect(() => {
    let cancelled = false;
    setCopied(false);
    if (!spd) {
      setPng(null);
      return;
    }
    (async () => {
      const QRCode = await import("qrcode");
      const url = await QRCode.toDataURL(spd, { margin: 2, width: 600, errorCorrectionLevel: "M" });
      if (!cancelled) setPng(url);
    })().catch(() => !cancelled && setPng(null));
    return () => {
      cancelled = true;
    };
  }, [spd]);

  const set = (field: keyof QrPaymentInput) => (value: string) =>
    setInput((prev) => ({ ...prev, [field]: value }));
  const errorOf = (field: keyof QrPaymentInput) => {
    if (!("errors" in result) || !touched[field]) return null;
    const code = result.errors[field];
    return code ? tr(ERRORS[code][0], ERRORS[code][1]) : null;
  };

  const fileStem = tr("qr-platba", "qr-payment");

  function used_(output: "png" | "svg" | "copy") {
    setUsed(true);
    track("tool_used", { tool: "qr_payment", output });
  }

  async function downloadSvg() {
    if (!spd) return;
    const QRCode = await import("qrcode");
    const svg = await QRCode.toString(spd, { type: "svg", margin: 2, errorCorrectionLevel: "M" });
    const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
    download(url, `${fileStem}.svg`);
    setTimeout(() => URL.revokeObjectURL(url), 0);
    used_("svg");
  }

  async function copySpd() {
    if (!spd) return;
    try {
      await navigator.clipboard.writeText(spd);
      setCopied(true);
      used_("copy");
    } catch {
      setCopied(false);
    }
  }

  const field = (
    name: keyof QrPaymentInput,
    label: string,
    props: React.ComponentProps<typeof Input> = {},
    hint?: string
  ) => {
    const error = errorOf(name);
    const inputId = `${id}-${name}`;
    return (
      <div>
        <Label htmlFor={inputId}>{label}</Label>
        <Input
          id={inputId}
          value={input[name]}
          onChange={(e) => set(name)(e.target.value)}
          onBlur={() => setTouched((t) => ({ ...t, [name]: true }))}
          aria-invalid={error ? true : undefined}
          aria-describedby={error ? `${inputId}-error` : hint ? `${inputId}-hint` : undefined}
          className={cn("mt-1.5", error && "border-destructive")}
          maxLength={name === "currency" ? undefined : QR_FIELD_MAX[name]}
          {...props}
        />
        {error ? (
          <p id={`${inputId}-error`} className="mt-1 text-xs text-destructive">
            {error}
          </p>
        ) : (
          hint && (
            <p id={`${inputId}-hint`} className="mt-1 text-xs text-muted-foreground">
              {hint}
            </p>
          )
        )}
      </div>
    );
  };

  return (
    <div className="rounded-xl border bg-card p-5 sm:p-6">
      <div className="grid gap-8 lg:grid-cols-[1fr_17rem]">
        <form className="grid gap-4 sm:grid-cols-2" onSubmit={(e) => e.preventDefault()} noValidate>
          <div className="sm:col-span-2">
            {field(
              "account",
              tr("Číslo účtu nebo IBAN", "Account number or IBAN"),
              { placeholder: "19-2000145399/0800", autoComplete: "off", spellCheck: false },
              tr(
                "Číslo účtu s kódem banky, nebo IBAN (CZ, SK).",
                "A Czech account number with bank code, or an IBAN (CZ, SK)."
              )
            )}
          </div>
          <div className="grid grid-cols-[1fr_6rem] gap-2">
            {field("amount", tr("Částka", "Amount"), {
              inputMode: "decimal",
              placeholder: "1 234,50",
            })}
            <div>
              <Label htmlFor={`${id}-currency`}>{tr("Měna", "Currency")}</Label>
              <select
                id={`${id}-currency`}
                value={input.currency}
                onChange={(e) => set("currency")(e.target.value)}
                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <option value="CZK">CZK</option>
                <option value="EUR">EUR</option>
              </select>
            </div>
          </div>
          {field("dueDate", tr("Datum splatnosti", "Due date"), { type: "date" })}
          {field("variableSymbol", tr("Variabilní symbol", "Variable symbol"), {
            inputMode: "numeric",
          })}
          {field("constantSymbol", tr("Konstantní symbol", "Constant symbol"), {
            inputMode: "numeric",
          })}
          {field("specificSymbol", tr("Specifický symbol", "Specific symbol"), {
            inputMode: "numeric",
          })}
          {field("recipientName", tr("Jméno příjemce", "Recipient name"), {})}
          <div className="sm:col-span-2">
            {field(
              "message",
              tr("Zpráva pro příjemce", "Message for the recipient"),
              {},
              tr(
                "Nejvýš 60 znaků; diakritiku QR platba nepřenese, proto ji vynecháme.",
                "At most 60 characters; QR payments carry no accents, so they are left out."
              )
            )}
          </div>
        </form>

        <div className="flex flex-col items-center gap-3 lg:items-stretch">
          <div className="flex aspect-square w-full max-w-[17rem] items-center justify-center rounded-lg border bg-white p-2">
            {png ? (
              <img src={png} alt={tr("QR platba", "QR payment")} className="size-full" />
            ) : (
              <p className="px-6 text-center text-sm text-neutral-500">
                {tr(
                  "QR kód se ukáže, jakmile vyplníte účet a částku.",
                  "The QR code appears once you fill in the account and amount."
                )}
              </p>
            )}
          </div>
          {"iban" in result && (
            <p className="text-center text-xs text-muted-foreground lg:text-left">
              IBAN {formatIban(result.iban)}
            </p>
          )}
          <div className="grid w-full max-w-[17rem] gap-2 lg:max-w-none">
            <Button
              type="button"
              disabled={!png}
              onClick={() => {
                if (!png) return;
                download(png, `${fileStem}.png`);
                used_("png");
              }}
            >
              <Download aria-hidden="true" className="mr-2 size-4" />
              {tr("Stáhnout PNG", "Download PNG")}
            </Button>
            <Button
              type="button"
              variant="outline"
              disabled={!spd}
              onClick={() => void downloadSvg()}
            >
              {tr("Stáhnout SVG", "Download SVG")}
            </Button>
          </div>
        </div>
      </div>

      {spd && (
        <div className="mt-6">
          <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
            {tr("Text QR platby (SPD)", "QR payment text (SPD)")}
          </p>
          <div className="mt-1.5 flex flex-col gap-2 sm:flex-row sm:items-center">
            <code className="min-w-0 flex-1 break-all rounded-md border bg-secondary/40 px-3 py-2 font-mono text-xs">
              {spd}
            </code>
            <Button type="button" variant="outline" size="sm" onClick={() => void copySpd()}>
              <Copy aria-hidden="true" className="mr-2 size-3.5" />
              {copied ? tr("Zkopírováno", "Copied") : tr("Kopírovat", "Copy")}
            </Button>
          </div>
        </div>
      )}

      <p className="mt-5 flex items-start gap-2 text-sm text-muted-foreground">
        <ShieldCheck
          aria-hidden="true"
          className="mt-0.5 size-4 shrink-0 text-[hsl(var(--action))] dark:text-primary"
        />
        {tr(
          "Nic z formuláře se nikam neodesílá: QR kód vzniká jen ve vašem prohlížeči.",
          "Nothing from the form is sent anywhere: the QR code is made only in your browser."
        )}
      </p>

      {used && (
        <p className="mt-5 rounded-lg border bg-secondary/40 px-4 py-3 text-sm leading-relaxed">
          {tr(
            "Chcete faktury s QR platbou vystavovat a příchozí platby z Fio k nim párovat automaticky? ",
            "Want to issue invoices with a QR payment and have incoming Fio payments matched to them automatically? "
          )}
          <Link
            to={localePath(locale, "/")}
            className="inline-flex items-center gap-1 font-medium text-[hsl(var(--action))] underline-offset-4 hover:underline dark:text-primary"
          >
            {tr("Podívejte se na Zettl", "Take a look at Zettl")}
            <ArrowRight aria-hidden="true" className="size-3.5" />
          </Link>
        </p>
      )}
    </div>
  );
}
